// The "Oficinas" directory — ported from mantis-business-front-brinks's src/routes/oficinas.tsx
// onto real data: `filterOffices` (BR-23's panel projection) instead of the mockup's static
// `mock-data.ts`, and `@mantis-sas/ui`'s real token names instead of brinks' own parallel design
// system (see `EstadoBadge.jsx`'s header for why those are not the same names).
//
// Deliberately NOT wired to `BusinessExtraction.jsx`'s route — a new screen alongside the
// existing Centro de Control, so nothing already working changes underneath it.
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Building2,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileUp,
  LayoutGrid,
  List,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Spinner,
  cn,
  toCsv,
  downloadCsv,
} from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { filterOffices, listCierreOfficeContacts } from "../../../ApiCalls/mantisBusiness";
import {
  ETIQUETA_ESTADO,
  estadoDeFila,
  fechaDMY,
  moneda,
  nombreOficinaDeMainKey,
  ultimoCierrePorOficina,
} from "../../../utils/cierrePanel";
import { EstadoBadge } from "./EstadoBadge";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { CargarCorreosExcel, EditarCorreosOficina } from "./CorreosOficinas";
import { GloryCelda, useGloryOficinas } from "./GloryOficina";
import { NuevaOficinaDialog } from "./NuevaOficinaDialog";
import { useClienteCierres } from "./ClienteCierresContext";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { PERMISO, filasExportables, puede } from "../../../utils/cierrePermisos";
import { avisoDeCreacion } from "../../../utils/cierreNuevaOficina";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

const ORDEN_ESTADO = { bloqueado: 0, pendiente: 1, cuadre: 2, sinCierre: 3 };

function filaDeRegistro(row) {
  return {
    id: row.closing_id || row.codigo_oficina,
    closingId: row.closing_id || null,
    codigo: row.codigo_oficina || "—",
    nombre: nombreOficinaDeMainKey(row.main_key),
    ciudad: row.ciudad || "—",
    region: row.region || "—",
    ultimo: fechaDMY(row.fecha_cuadre),
    fechaCuadre: row.fecha_cuadre || "",
    diferencia: row.diferencia_cuadre,
    estado: estadoDeFila(row),
    raw: row,
  };
}

// An office of the client with no closing yet (`listCierreOfficeContacts` lists every office): a
// row of the directory all the same, "Sin cierres aún", with nothing to open — one just created with
// "Añadir oficina" shows here at once.
function filaSinCierre(oficina) {
  const codigo = String(oficina.officeCode);
  return {
    id: `oficina-${codigo}`,
    closingId: null,
    codigo,
    nombre: oficina.officeName || codigo,
    ciudad: "—",
    region: oficina.region || "—",
    ultimo: "—",
    fechaCuadre: "",
    diferencia: null,
    estado: "sinCierre",
    raw: { codigo_oficina: codigo, region: oficina.region || null },
  };
}

function CierresOficinas(props) {
  // B-55 — which client the shell's selector is on. Every row below is that client's.
  const { clientCode, listo } = useClienteCierres();
  // BR-38 — export is asked of this client, and writes only the rows whose office it reaches
  // (`filasExportables`): no route on the server sees an export, so this is the only check.
  const acceso = useAccesoCierres();
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  const [cargando, setCargando] = useState(true);
  const [filasDeCierres, setFilasDeCierres] = useState([]);
  const [vista, setVista] = useState("lista");
  const [q, setQ] = useState("");
  const [ciudad, setCiudad] = useState("todas");
  const [region, setRegion] = useState("todas");
  const [estado, setEstado] = useState("todos");
  const [orden, setOrden] = useState({ campo: "nombre", dir: "asc" });
  const [sel, setSel] = useState([]);
  const [detalleClosingId, setDetalleClosingId] = useState(null);
  // B-89 — each office's own addresses (`listCierreOfficeContacts`): every office of the client,
  // with or without a closing, and whether this person may change them (`editable`, the server's).
  const [contactos, setContactos] = useState([]);
  const [versionContactos, setVersionContactos] = useState(0);
  const [editandoCorreos, setEditandoCorreos] = useState(null);
  const [cargandoExcel, setCargandoExcel] = useState(false);
  // "Añadir oficina" — a new office of a client with GLORY machines, for whoever configures offices
  // on the whole client (the backend asks the same).
  const [creandoOficina, setCreandoOficina] = useState(false);
  // The line under the count that says what was just saved: addresses (B-89) or a GLORY (B-134).
  const [aviso, setAviso] = useState(null);
  // B-80 — bumped after a closing is redone from its detail, so the screen reads it again.
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    // B-55 — wait for the client list: a fetch that goes out without a `clientCode` is the 400
    // a business with two cierre clients answers with.
    if (!listo) return undefined;

    let activo = true;
    (async () => {
      setCargando(true);
      const response = await filterOffices(
        props.token,
        {},
        500,
        props.mantisBusiness._id,
        "brinks",
        clientCode
      ).catch(() => null);
      if (!activo) return;
      if (response?.success) {
        setFilasDeCierres(ultimoCierrePorOficina(response.data).map(filaDeRegistro));
      }
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo, recarga]);

  useEffect(() => {
    if (!listo) return undefined;
    let activo = true;
    (async () => {
      const response = await listCierreOfficeContacts(props.token, props.mantisBusiness._id, clientCode).catch(() => null);
      if (activo) setContactos(response?.success ? response.data.offices || [] : []);
    })();
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo, versionContactos]);

  // B-134 — each office's "Máquina GLORY" switch; the column is drawn only for a client that has one.
  const glory = useGloryOficinas({
    token: props.token,
    mantisBusinessId: props.mantisBusiness._id,
    clientCode,
    listo,
    onGuardado: (texto) => {
      setAviso(texto);
      setRecarga((n) => n + 1);
    },
  });

  const filas = useMemo(() => {
    const conCierre = new Set(filasDeCierres.map((f) => String(f.codigo)));
    return [
      ...filasDeCierres,
      ...contactos.filter((o) => !conCierre.has(String(o.officeCode))).map(filaSinCierre),
    ];
  }, [filasDeCierres, contactos]);
  const contactoDe = useMemo(() => new Map(contactos.map((o) => [String(o.officeCode), o])), [contactos]);
  const puedeEditarCorreos = contactos.some((o) => o.editable);
  const puedeCrearOficina =
    glory.soportado && puede(acceso, PERMISO.CONFIGURAR_OFICINAS, { cliente: clientCode, entero: true });
  const oficinaCreada = (data) => {
    setCreandoOficina(false);
    setAviso(avisoDeCreacion(data));
    setRecarga((n) => n + 1);
    setVersionContactos((n) => n + 1);
    glory.recargar();
  };
  const correosGuardados = (texto) => {
    setEditandoCorreos(null);
    setCargandoExcel(false);
    setAviso(texto);
    setVersionContactos((n) => n + 1);
  };

  const ciudades = useMemo(
    () => [...new Set(filas.map((f) => f.ciudad).filter((c) => c && c !== "—"))].sort(),
    [filas]
  );
  const regiones = useMemo(
    () => [...new Set(filas.map((f) => f.region).filter((r) => r && r !== "—"))].sort(),
    [filas]
  );
  // The new office's choices: every region and code the client has, with or without a closing.
  const regionesDelCliente = useMemo(
    () => [...new Set([...regiones, ...contactos.map((o) => o.region).filter(Boolean)])].sort(),
    [regiones, contactos]
  );
  const codigosDelCliente = useMemo(
    () => [...new Set([...filas.map((f) => f.codigo), ...contactos.map((o) => String(o.officeCode))])],
    [filas, contactos]
  );

  const filtradas = useMemo(() => {
    const texto = q.trim().toLowerCase();
    const res = filas.filter((f) => {
      if (
        texto &&
        !`${f.nombre} ${f.codigo} ${f.ciudad} ${f.region}`.toLowerCase().includes(texto)
      )
        return false;
      if (ciudad !== "todas" && f.ciudad !== ciudad) return false;
      if (region !== "todas" && f.region !== region) return false;
      if (estado !== "todos" && f.estado !== estado) return false;
      return true;
    });
    const dir = orden.dir === "asc" ? 1 : -1;
    return [...res].sort((a, b) => {
      if (orden.campo === "estado")
        return (ORDEN_ESTADO[a.estado] - ORDEN_ESTADO[b.estado]) * dir;
      const va = a[orden.campo];
      const vb = b[orden.campo];
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filas, q, ciudad, region, estado, orden]);

  const resumen = useMemo(
    () => ({
      total: filas.length,
      cuadre: filas.filter((f) => f.estado === "cuadre").length,
      pendiente: filas.filter((f) => f.estado === "pendiente").length,
      bloqueado: filas.filter((f) => f.estado === "bloqueado").length,
    }),
    [filas]
  );

  const hayFiltros = q !== "" || ciudad !== "todas" || region !== "todas" || estado !== "todos";

  const reiniciar = () => {
    setQ("");
    setCiudad("todas");
    setRegion("todas");
    setEstado("todos");
  };

  const ordenar = (campo) =>
    setOrden((prev) => ({
      campo,
      dir: prev.campo === campo && prev.dir === "asc" ? "desc" : "asc",
    }));

  const todasVisiblesSel = filtradas.length > 0 && filtradas.every((f) => sel.includes(f.id));
  const toggleTodas = () =>
    setSel(todasVisiblesSel ? [] : filtradas.map((f) => f.id));
  const toggleUna = (id) =>
    setSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const exportar = (todas) => {
    const fs = filasExportables(acceso, clientCode, todas, (f) => ({ oficina: f.raw.codigo_oficina, region: f.raw.region }));
    const header = ["Codigo", "Oficina", "Ciudad", "Region", "Ultimo cierre", "Diferencia", "Estado"];
    const rows = fs.map((f) => [f.codigo, f.nombre, f.ciudad, f.region, f.ultimo, f.diferencia ?? "", f.estado]);
    downloadCsv("oficinas.csv", toCsv(header, rows));
  };

  const indicadores = [
    { clave: "todos", etiqueta: "Oficinas", valor: resumen.total },
    { clave: "cuadre", etiqueta: "En cuadre", valor: resumen.cuadre },
    { clave: "pendiente", etiqueta: "Pendientes", valor: resumen.pendiente },
    { clave: "bloqueado", etiqueta: "Bloqueadas", valor: resumen.bloqueado },
  ];

  return (
    <div className="brinks-scope font-redhat min-w-0 flex-1 bg-background p-3 sm:p-4">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-lora text-[21px] font-semibold text-brinks-foreground">Oficinas</h1>
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">
            Directorio operativo y estado del último cierre
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div {...guia("Vista", "Cambia entre la lista y las tarjetas.")} className="hidden h-8 items-center rounded-[8px] border bg-card p-0.5 lg:flex">
            {["lista", "tarjetas"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVista(v)}
                aria-pressed={vista === v}
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 rounded-[6px] px-2.5 text-[12px] font-medium capitalize transition",
                  vista === v
                    ? "bg-primary text-primary-foreground"
                    : "text-brinks-muted-foreground hover:bg-brinks-surface-hover"
                )}
              >
                {v === "lista" ? (
                  <List className="h-3.5 w-3.5" />
                ) : (
                  <LayoutGrid className="h-3.5 w-3.5" />
                )}
                <span className="hidden sm:inline">{v}</span>
              </button>
            ))}
          </div>
          {puedeCrearOficina ? (
            <Button variant="outline" size="sm" className="h-8 gap-1.5" {...guia("Añadir oficina", "Registra una oficina nueva: municipio, regional, cupo, correos y si tiene máquina GLORY.")} onClick={() => setCreandoOficina(true)}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Añadir oficina</span>
            </Button>
          ) : null}
          {puedeEditarCorreos ? (
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setCargandoExcel(true)}>
              <FileUp className="h-4 w-4" />
              <span className="hidden sm:inline">Cargar correos</span>
            </Button>
          ) : null}
          {puedeExportar ? (
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => exportar(filtradas)}>
              <FileSpreadsheet className="h-4 w-4" />
              <span className="hidden sm:inline">Exportar</span>
            </Button>
          ) : null}
        </div>
      </header>

      <div {...guia("Estados", "Cuántas oficinas cuadran, están pendientes o bloqueadas. Toca uno para filtrar.")} className="mb-2 grid grid-cols-2 gap-1.5 lg:grid-cols-4">
        {indicadores.map((i) => {
          const activo = estado === i.clave || (i.clave === "todos" && estado === "todos");
          return (
            <button
              key={i.clave}
              type="button"
              onClick={() => setEstado(i.clave)}
              className={cn(
                "flex items-baseline justify-between gap-2 overflow-hidden rounded-[10px] border bg-card px-3 py-2 text-left transition hover:bg-brinks-surface-hover",
                activo && "border-foreground/30"
              )}
            >
              <span className="min-w-0 truncate text-[12px] text-brinks-muted-foreground">
                {i.etiqueta}
              </span>
              <span className="shrink-0 text-[16px] font-semibold tabular-nums">{i.valor}</span>
            </button>
          );
        })}
      </div>

      <div {...guia("Filtros", "Busca por oficina, código o ciudad, y filtra por ciudad, región o estado.")} className="mb-2 rounded-[10px] border bg-card p-1.5">
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))_auto]">
          <div className="relative min-w-0 sm:col-span-2 lg:col-span-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar oficina, código o ciudad..."
              className="h-8 w-full rounded-[8px] border bg-card pl-8 pr-2 text-[13px] outline-none focus:border-brinks-border-strong"
            />
          </div>
          <Select value={ciudad} onValueChange={setCiudad}>
            <SelectTrigger className="h-8 rounded-[8px] text-[13px]">
              <SelectValue>{ciudad === "todas" ? "Todas las ciudades" : ciudad}</SelectValue>
            </SelectTrigger>
            <SelectContent className="brinks-scope font-redhat">
              <SelectItem value="todas">Todas las ciudades</SelectItem>
              {ciudades.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="h-8 rounded-[8px] text-[13px]">
              <SelectValue>{region === "todas" ? "Todas las regiones" : region}</SelectValue>
            </SelectTrigger>
            <SelectContent className="brinks-scope font-redhat">
              <SelectItem value="todas">Todas las regiones</SelectItem>
              {regiones.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="h-8 rounded-[8px] text-[13px]">
              <SelectValue>
                {estado === "todos" ? "Todos los estados" : ETIQUETA_ESTADO[estado] || estado}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="brinks-scope font-redhat">
              <SelectItem value="todos">Todos los estados</SelectItem>
              <SelectItem value="cuadre">Cuadre</SelectItem>
              <SelectItem value="pendiente">Pendiente revisión</SelectItem>
              <SelectItem value="bloqueado">Bloqueado</SelectItem>
              <SelectItem value="sinCierre">Sin cierres aún</SelectItem>
            </SelectContent>
          </Select>
          {hayFiltros ? (
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-[12px]" onClick={reiniciar}>
              <RotateCcw className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Restablecer</span>
            </Button>
          ) : null}
        </div>
      </div>

      <p className="mb-1.5 text-[12px] text-brinks-muted-foreground">
        {filtradas.length} de {filas.length} oficinas
      </p>
      {aviso ? (
        <p role="status" className="mb-1.5 text-[12px] text-brinks-success">
          {aviso}
        </p>
      ) : null}

      {cargando ? (
        <div className="grid place-items-center gap-2 rounded-[12px] border border-dashed bg-card py-14 text-center">
          <Spinner size="lg" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="grid place-items-center gap-2 rounded-[12px] border border-dashed bg-card py-14 text-center">
          <Building2 className="h-6 w-6 text-brinks-muted-foreground" />
          <p className="text-[13px] text-brinks-muted-foreground">
            {filas.length === 0
              ? "Todavía no hay cierres registrados para ninguna oficina."
              : "Ninguna oficina coincide con los filtros."}
          </p>
          {hayFiltros ? (
            <Button variant="outline" size="sm" className="h-8" onClick={reiniciar}>
              Restablecer filtros
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          {vista === "lista" ? (
            <div {...guia("Oficinas", "Cada oficina con sus correos y su máquina GLORY. Desde aquí editas sus correos o abres su último cierre.")} className="hidden w-full overflow-hidden rounded-[12px] border bg-card lg:block">
              <Table className="w-full table-fixed">
                {glory.soportado ? (
                  <colgroup>
                    <col className="w-[36px]" />
                    <col className="w-[16%]" />
                    <col className="w-[8%]" />
                    <col className="w-[8%]" />
                    <col className="w-[13%]" />
                    <col className="w-[20%]" />
                    <col className="w-[9%]" />
                    <col className="w-[9%]" />
                    <col className="w-[13%]" />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col className="w-[36px]" />
                    <col className="w-[21%]" />
                    <col className="w-[11%]" />
                    <col className="w-[11%]" />
                    <col className="w-[19%]" />
                    <col className="w-[10%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                  </colgroup>
                )}
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 px-2">
                      <Checkbox checked={todasVisiblesSel} onCheckedChange={toggleTodas} aria-label="Seleccionar todas" />
                    </TableHead>
                    <Th campo="nombre" orden={orden} onClick={ordenar}>Oficina</Th>
                    <Th campo="ciudad" orden={orden} onClick={ordenar}>Ciudad</Th>
                    <Th campo="region" orden={orden} onClick={ordenar}>Región</Th>
                    <TableHead className="h-9 px-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                      Correos
                    </TableHead>
                    {glory.soportado ? (
                      <TableHead className="h-9 px-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                        Máquina GLORY
                      </TableHead>
                    ) : null}
                    <Th campo="ultimo" orden={orden} onClick={ordenar}>Fecha</Th>
                    <Th campo="diferencia" orden={orden} onClick={ordenar} alineado>Diferencia</Th>
                    <Th campo="estado" orden={orden} onClick={ordenar}>Estado</Th>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtradas.map((f) => (
                    <TableRow
                      key={f.id}
                      className="cursor-pointer"
                      onClick={() => f.closingId && setDetalleClosingId(f.closingId)}
                    >
                      <TableCell className="px-2 py-0" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={sel.includes(f.id)}
                          onCheckedChange={() => toggleUna(f.id)}
                          aria-label={`Seleccionar ${f.nombre}`}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium">{f.nombre}</p>
                          <p className="truncate text-[11.5px] text-brinks-muted-foreground">{f.codigo}</p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-0 truncate text-[13px]">{f.ciudad}</TableCell>
                      <TableCell className="max-w-0 truncate text-[13px] text-brinks-muted-foreground">
                        {f.region}
                      </TableCell>
                      <TableCell className="max-w-0 py-2" onClick={(e) => e.stopPropagation()}>
                        <CorreosCelda
                          contacto={contactoDe.get(String(f.codigo))}
                          onEditar={(c) => setEditandoCorreos(c)}
                        />
                      </TableCell>
                      {glory.soportado ? (
                        <TableCell className="max-w-0 py-2" onClick={(e) => e.stopPropagation()}>
                          <GloryCelda
                            sinFirma
                            estado={glory.gloryDe.get(String(f.codigo))}
                            onCambiar={glory.cambiar}
                            onHistorial={glory.verHistorial}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="whitespace-nowrap text-[13px] tabular-nums">
                        {f.ultimo}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "whitespace-nowrap text-right text-[13px] font-medium tabular-nums",
                          f.diferencia != null && Number(f.diferencia) !== 0
                            ? "text-brinks-danger"
                            : "text-brinks-muted-foreground"
                        )}
                      >
                        {moneda(f.diferencia)}
                      </TableCell>
                      <TableCell className="max-w-0 py-2">
                        <div className="flex min-w-0">
                          <EstadoBadge estado={f.estado} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          <div {...guia("Oficinas", "Cada oficina con sus correos y su máquina GLORY. Desde aquí editas sus correos o abres su último cierre.")} className={cn("grid gap-2 sm:grid-cols-2 xl:grid-cols-3", vista === "lista" && "lg:hidden")}>
            {filtradas.map((f) => (
              <article
                key={f.id}
                onClick={() => f.closingId && setDetalleClosingId(f.closingId)}
                className="cursor-pointer overflow-hidden rounded-[12px] border bg-card p-3 transition hover:bg-brinks-surface-hover"
              >
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={sel.includes(f.id)}
                      onCheckedChange={() => toggleUna(f.id)}
                      aria-label={`Seleccionar ${f.nombre}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[13.5px] font-medium">{f.nombre}</p>
                    <p className="truncate text-[11.5px] text-brinks-muted-foreground">
                      {f.codigo} · {f.ciudad}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex">
                  <EstadoBadge estado={f.estado} />
                </div>
                <dl className="mt-2.5 space-y-1 border-t pt-2 text-[12.5px]">
                  <Linea k="Último cierre" v={f.ultimo} />
                  <Linea k="Correos" v={(contactoDe.get(String(f.codigo))?.emails || []).join(", ") || "—"} />
                  {glory.soportado ? (
                    <div className="flex items-start justify-between gap-3" onClick={(e) => e.stopPropagation()}>
                      <dt className="shrink-0 pt-0.5 text-brinks-muted-foreground">Máquina GLORY</dt>
                      <dd className="flex min-w-0 justify-end">
                        <GloryCelda
                          estado={glory.gloryDe.get(String(f.codigo))}
                          onCambiar={glory.cambiar}
                          onHistorial={glory.verHistorial}
                          className="text-right"
                        />
                      </dd>
                    </div>
                  ) : null}
                  <Linea
                    k="Diferencia"
                    v={moneda(f.diferencia)}
                    clase={f.diferencia != null && Number(f.diferencia) !== 0 ? "text-brinks-danger" : undefined}
                  />
                </dl>
              </article>
            ))}
          </div>
        </>
      )}

      {sel.length > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-30 flex justify-center px-3">
          <div className="pointer-events-auto flex items-center gap-2 rounded-[12px] bg-primary px-3 py-2 text-primary-foreground shadow-lg">
            <span className="text-[12.5px]">{sel.length} seleccionadas</span>
            {puedeExportar ? (
              <Button
                size="sm"
                variant="secondary"
                className="h-7 gap-1.5 text-[12px]"
                onClick={() => exportar(filtradas.filter((f) => sel.includes(f.id)))}
              >
                <Download className="h-3.5 w-3.5" /> Exportar
              </Button>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-primary-foreground hover:bg-white/10"
              onClick={() => setSel([])}
              aria-label="Limpiar selección"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}

      {editandoCorreos ? (
        <EditarCorreosOficina
          oficina={editandoCorreos}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          onClose={() => setEditandoCorreos(null)}
          onSaved={() => correosGuardados(`Correos de ${editandoCorreos.officeName} guardados.`)}
        />
      ) : null}
      {cargandoExcel ? (
        <CargarCorreosExcel
          open
          oficinas={contactos}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          onClose={() => setCargandoExcel(false)}
          onSaved={(n) => correosGuardados(`Correos guardados para ${n} ${n === 1 ? "oficina" : "oficinas"}.`)}
        />
      ) : null}

      {glory.dialogos}
      <NuevaOficinaDialog
        open={creandoOficina}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        clientCode={clientCode}
        regiones={regionesDelCliente}
        codigosExistentes={codigosDelCliente}
        conGlory={glory.soportado}
        onClose={() => setCreandoOficina(false)}
        onCreated={oficinaCreada}
      />

      <CierreDetailDialog
        closingId={detalleClosingId}
        panelRow={filas.find((f) => f.closingId === detalleClosingId)?.raw}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => !open && setDetalleClosingId(null)}
        onCambio={() => {
          setRecarga((n) => n + 1);
          glory.recargar();
        }}
      />
    </div>
  );
}

function Th({ campo, orden, onClick, children, alineado }) {
  const activo = orden.campo === campo;
  return (
    <TableHead className="h-9 px-2">
      <button
        type="button"
        onClick={() => onClick(campo)}
        className={cn(
          "inline-flex w-full items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.04em] transition hover:text-brinks-foreground",
          alineado && "justify-end",
          activo ? "text-brinks-foreground" : "text-brinks-muted-foreground"
        )}
      >
        <span className="truncate">{children}</span>
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 transition",
            !activo && "opacity-0",
            activo && orden.dir === "asc" && "rotate-180"
          )}
        />
      </button>
    </TableHead>
  );
}

// B-89 — an office's addresses in the list: the first one, how many more, and the pencil for whoever
// may change them. Clicking it does not open the closing behind the row.
function CorreosCelda({ contacto, onEditar }) {
  const correos = contacto?.emails || [];
  return (
    <div className="flex min-w-0 items-center gap-1">
      <span
        className={cn("min-w-0 truncate text-[12.5px]", correos.length ? "text-brinks-foreground" : "text-brinks-muted-foreground")}
        title={correos.join(", ") || undefined}
      >
        {correos.length ? correos[0] : "Sin correos"}
        {correos.length > 1 ? <span className="text-brinks-muted-foreground"> +{correos.length - 1}</span> : null}
      </span>
      {contacto?.editable ? (
        <button
          type="button"
          onClick={() => onEditar(contacto)}
          aria-label={`Editar correos de ${contacto.officeName}`}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-[6px] text-brinks-muted-foreground hover:bg-brinks-surface-hover hover:text-brinks-foreground"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function Linea({ k, v, clase }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-brinks-muted-foreground">{k}</dt>
      <dd className={cn("min-w-0 truncate whitespace-nowrap text-right tabular-nums", clase)}>{v}</dd>
    </div>
  );
}

export default connect(selectAuthState)(CierresOficinas);
